import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { LOCALES, LOCALE_TAGS, localizePath, splitLocalePath } from '@shop/shared';
import { useSettings } from './settings';
import { assetUrl } from './api';
import { useLocale } from './i18n';

interface Meta {
  title?: string;
  description?: string;
  image?: string | null;
  type?: 'website' | 'product' | 'article';
  jsonLd?: Record<string, unknown> | null;
  noindex?: boolean;
}

function setMeta(attr: 'name' | 'property', key: string, content: string | null | undefined) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setAlternate(hreflang: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = 'alternate';
    el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

/** Client-side SEO: title, description, Open Graph tags, canonical URL, language alternates and JSON-LD. */
export function useSeo(meta: Meta) {
  const { seo } = useSettings();
  const locale = useLocale();
  const { pathname } = useLocation();
  const basePath = splitLocalePath(pathname).path;
  const title = meta.title ? seo.titleTemplate.replace('%s', meta.title) : seo.title;
  const description = meta.description || seo.description;
  const image = meta.image ?? seo.ogImageUrl;
  const jsonLd = meta.jsonLd ? JSON.stringify(meta.jsonLd) : null;

  useEffect(() => {
    document.title = title;
    const abs = (u: string | null | undefined) => (u ? new URL(assetUrl(u), window.location.origin).toString() : null);
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', meta.type ?? 'website');
    setMeta('property', 'og:url', window.location.href.split('?')[0]);
    setMeta('property', 'og:image', abs(image));
    setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('name', 'robots', meta.noindex ? 'noindex' : null);
    setMeta('property', 'og:locale', LOCALE_TAGS[locale].replace('-', '_'));
    for (const l of LOCALES) setAlternate(l, window.location.origin + localizePath(basePath, l));
    setAlternate('x-default', window.location.origin + basePath);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + window.location.pathname;

    const id = 'page-jsonld';
    document.getElementById(id)?.remove();
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = jsonLd;
      document.head.appendChild(script);
    }
  }, [title, description, image, meta.type, meta.noindex, jsonLd, locale, basePath]);
}
